import { TRANSFERS_PER_PAGE } from '../constants';
import { getApi } from '@wetransfer/transfer/apollo-api/src';
import { GET_SENT_TRANSFERS } from '@wetransfer/transfer/apollo-api/usage/sections/transfer/getSentTransfers';
import { SentTransfersQueryVariables } from '@wetransfer/transfer/apollo-api/usage/types';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import TransferListRouteParamsHelper from '@wetransfer/transfer/hooks/useTransferListRouteParams/helper';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { transformTransferAPIToGraphQL } from '@wetransfer/transfer/transfer-bundle/helpers/convert-api-transfer-to-graphql';
import {
  DownloadTransferResponseRaw,
  OwnedTransferResponseRaw,
} from '@wetransfer/transfer/transfer-bundle/types/transfers';
import {
  OrderDirection,
  SentTransfersOrderAttributes,
  User,
} from '@wetransfer/transfer/types/graphql-types';

export const updateCacheAfterEditingTitle = (
  transferId: string,
  title: string
): void => {
  const client = getApi();

  client.cache.modify({
    id: `Transfer:${transferId}`,
    fields: {
      name() {
        return title;
      },
    },
    broadcast: true,
  });
};

export const updateCacheAfterDeletingTransfers = (
  transferIds: string[]
): void => {
  const client = getApi();

  for (let i = 0; i < transferIds.length; i++) {
    client.cache.evict({
      id: `Transfer:${transferIds[i]}`,
      broadcast: true,
    });
  }
};

export const refillTransfersAfterDeletion = (
  variables: SentTransfersQueryVariables,
  userData: User
): void => {
  const client = getApi();

  client.writeQuery({
    query: GET_SENT_TRANSFERS,
    variables,
    data: {
      currentUser: userData,
    },
  });
};

export const resetTransfersStateAfterDeletion = (
  variables: SentTransfersQueryVariables
): void => {
  const client = getApi();

  const data = client.readQuery({
    query: GET_SENT_TRANSFERS,
    variables,
  });

  if (data?.currentUser) {
    const resetCurrentUser = {
      ...data.currentUser,
      team: {
        ...data.currentUser.team,
        sentTransfers: {
          ...data.currentUser.team.sentTransfers,
          totalCount: 0,
          collection: [],
        },
      },
    };

    client.writeQuery({
      query: GET_SENT_TRANSFERS,
      variables,
      data: {
        currentUser: resetCurrentUser,
      },
    });
  }
};

export const addTransferAfterUpload = (
  transfer: OwnedTransferResponseRaw & DownloadTransferResponseRaw
): void => {
  const client = getApi();

  const { sortOrder, sortBy, search, p } =
    TransferListRouteParamsHelper.getParamsFromUrl();

  const currentTeamId = Cookie.get(AppConfig.cookies.currentTeam.name);

  const sentTransfersQueryVariables = {
    teamId: currentTeamId,
    limit: TRANSFERS_PER_PAGE,
    order: {
      direction: sortOrder as OrderDirection,
      field: sortBy as SentTransfersOrderAttributes,
    },
    searchTerm: search || '',
    highlight: Boolean(search),
    page: parseInt(p || '1'),
  } as SentTransfersQueryVariables;

  const data = client.readQuery({
    query: GET_SENT_TRANSFERS,
    variables: sentTransfersQueryVariables,
  });

  // If transfer page was not opened, this will be "null"
  if (data?.currentUser) {
    const newTransfer = transformTransferAPIToGraphQL(transfer);

    const transferCollection = [
      ...data.currentUser.team.sentTransfers.collection,
    ];

    // Add transfer at the beginning of Transfer List
    transferCollection.unshift(newTransfer);

    // Remove last item to keep the same count per page
    if (transferCollection.length > TRANSFERS_PER_PAGE) {
      transferCollection.pop();
    }

    const updatedCurrentUser = {
      ...data.currentUser,
      team: {
        ...data.currentUser.team,
        sentTransfers: {
          ...data.currentUser.team.sentTransfers,
          totalCount: data.currentUser.team.sentTransfers.totalCount + 1,
          collection: transferCollection,
        },
      },
    };

    client.writeQuery({
      query: GET_SENT_TRANSFERS,
      variables: sentTransfersQueryVariables,
      data: {
        currentUser: updatedCurrentUser,
      },
    });
  }
};
